import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { sortByDistance } from "../../modules/Store/distanceCalculator";
import {
  getStoreTags,
  StoreCoordinates,
  storesMapper,
} from "../../modules/Store/storeMapper";
import { getStoreById } from "../../modules/Store/utils";
import { ErrorResponse } from "../error/models";
import { AllActionsOf } from "../utils/Actions";
import {
  GetStoreOrderTimeslotsAndOffsetRequestProps,
  GetStoreOrderTimeslotsAndOffsetResponseProps,
  GetStoresSuccessProps,
  GooglePlaceDetailSearch,
  NearestStoreProps,
  SortStoreByDistanceProps,
  Store,
  StoreOpenTime,
  StoreSearchPayload,
  StoreSearchResult,
  StoreState,
} from "./models";

const initialState: StoreState = {
  selectedStore: null,
  stores: [],
  storeTags: [],
  getStoresLoading: false,
  searchResult: { goolePlaceSearchResult: [], fuzzySearchResult: [] },
  storeSearchLocation: null,
  showStoreInfo: null,
  storeOpenTime: {
    isOpen: true,
    tradingTimeInfo: "",
  },
  error: undefined,

  getStoreOrderLoading: false,
  storeOrderTimeSlotsError: undefined,
  storeOrderTimes: [],
  storeOrderOffset: [],
};

const storeSlice = createSlice({
  name: "store",
  initialState: initialState,
  reducers: {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    storeSearch(_state, _action: PayloadAction<StoreSearchPayload>) {},
    storeSearchSuccess(state, action: PayloadAction<StoreSearchResult>) {
      state.searchResult = action.payload;
    },
    googlePlaceDetailSearch(
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      _state,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      _action: PayloadAction<GooglePlaceDetailSearch>
    ) {},
    googlePlaceDetailSearchSuccess(
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      _state,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      _action: PayloadAction<SortStoreByDistanceProps>
    ) {},
    setStoreSearchLocation(
      state,
      action: PayloadAction<StoreCoordinates | null>
    ) {
      state.storeSearchLocation = action.payload;
    },
    setStore(state, action: PayloadAction<Store>) {
      state.selectedStore = {
        ...action.payload,
      };
    },
    getStores(state) {
      state.getStoresLoading = true;
    },
    handleApiFailure(state, action: PayloadAction<ErrorResponse>) {
      state.getStoresLoading = false;
      state.error = action.payload;
    },
    getStoresSuccess(state, action: PayloadAction<GetStoresSuccessProps>) {
      state.stores = storesMapper(action.payload).sort((a, b) =>
        a.name.localeCompare(b.name)
      );
      state.getStoresLoading = false;
      state.storeTags = getStoreTags(state.stores);
      state.error = undefined;
    },
    getStoreOrderTimeslotsAndOffset(
      state,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      _action: PayloadAction<GetStoreOrderTimeslotsAndOffsetRequestProps>
    ) {
      state.getStoreOrderLoading = true;
    },
    getStoreOrderTimeslotsAndOffsetSuccess(
      state,
      action: PayloadAction<GetStoreOrderTimeslotsAndOffsetResponseProps>
    ) {
      state.getStoreOrderLoading = false;
      state.storeOrderTimeSlotsError = undefined;
      state.storeOrderTimes = action.payload.orderTimes;
      state.storeOrderOffset = action.payload.orderOffset;
    },
    getStoreOrderTimeslotsAndOffsetFailure(
      state,
      action: PayloadAction<ErrorResponse>
    ) {
      state.getStoreOrderLoading = false;
      state.storeOrderTimeSlotsError = action.payload;
      state.storeOrderTimes = [];
      state.storeOrderOffset = [];
    },
    sortStoresByDistance(
      state,
      action: PayloadAction<SortStoreByDistanceProps>
    ) {
      state.stores = sortByDistance(
        state.stores,
        action.payload,
        action.payload.version
      );
    },
    setNearestStoreAsSelected(state, action: PayloadAction<NearestStoreProps>) {
      if (
        (state.stores.length > 0 &&
          state.stores[0].distance <=
            action.payload.nearestStoreThresholdKm * 1000) ||
        action.payload.nearestStoreThresholdKm === 0
      ) {
        state.selectedStore = state.stores[0];
      }
    },
    setStoreInfo(state, action: PayloadAction<Store | null>) {
      state.showStoreInfo = action.payload;
    },
    selectStoreById(state, action: PayloadAction<string>) {
      const storeId = Number(action.payload);
      state.selectedStore = getStoreById(storeId, state.stores);
    },
    setStoreOpenTime(state, action: PayloadAction<StoreOpenTime>) {
      state.storeOpenTime = action.payload;
    },
  },
});

const { actions, reducer } = storeSlice;
export type StoreActionsType = AllActionsOf<typeof actions>;
export { initialState, actions as storeActions, reducer as storeReducer };
